// src/pages/Cart.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cartAPI, deliveryAPI, productsAPI } from '../services/api';
import { X, Minus, Plus, Trash2, ShoppingCart, CreditCard, Check, Package, Truck, Settings, ChevronDown, ChevronUp } from 'lucide-react';
import './css/ShoppingCart.css';

const Cart = ({ closeCart }) => {
  const [cartItems, setCartItems] = useState([]);
  const [deliverySettings, setDeliverySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cartResponse, settingsResponse] = await Promise.all([
          cartAPI.getCart(),
          deliveryAPI.getSettings()
        ]);
        let items = cartResponse.data;
        setDeliverySettings(settingsResponse.data);

        // Fetch full product details for ALL items to ensure accurate pricing
        const uniqueProductIds = [...new Set(items.map(item => item.productId))];
        const productResponses = await Promise.allSettled(
          uniqueProductIds.map(id => productsAPI.getById(id))
        );
        const productsMap = {};
        productResponses.forEach((resp, index) => {
          if (resp.status === 'fulfilled') {
            productsMap[uniqueProductIds[index]] = resp.value.data;
          }
        });

        // Map all items with full product details
        items = items.map(item => {
          const fullProduct = productsMap[item.productId];
          if (!fullProduct) {
            // Fallback if product fetch failed
            return {
              ...item,
              name: item.name || 'Produit inconnu',
              imageUrl: item.imageUrl,
              price: Number(item.price || 0),
            };
          }

          let basePrice = Number(fullProduct.salePrice || fullProduct.regularPrice || 0);
          let optionExtra = 0;
          let options = fullProduct.options || null;

          if (options && item.selected_options && Object.keys(item.selected_options).length > 0) {
            options.forEach(group => {
              const selectedValueId = item.selected_options[group.id];
              if (selectedValueId) {
                const value = group.values.find(v => v.id === Number(selectedValueId));
                if (value?.extra_price) {
                  optionExtra += Number(value.extra_price);
                }
              }
            });
          }

          const finalPrice = basePrice + optionExtra;

          return {
            ...item,
            name: fullProduct.name || item.name || 'Produit inconnu',
            imageUrl: fullProduct.imageUrl || item.imageUrl,
            price: finalPrice,
            options,
            basePrice,
            optionExtra
          };
        });

        setCartItems(items);
      } catch (err) {
        console.error('Failed to load cart:', err);
        setDeliverySettings({ delivery_fee: 0, min_free_delivery: 0, is_free_for_all: false, is_enabled: false });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => closeCart(), 300);
  };

  const handleUpdateQuantity = async (productId, newQuantity, selectedOptions) => {
    if (newQuantity < 1) return;
    try {
      await cartAPI.updateQuantity({ productId, quantity: newQuantity, selected_options: selectedOptions });
      setCartItems(prev => prev.map(item =>
        item.productId === productId ? { ...item, quantity: newQuantity } : item
      ));
    } catch (err) {
      alert(err.response?.data?.message || "Echec de la mise a jour");
    }
  };

  const handleDeleteItem = async (productId) => {
    try {
      await cartAPI.deleteItem(productId);
      setCartItems(prev => prev.filter(item => item.productId !== productId));
    } catch (err) {
      alert("Echec de la suppression de l'article");
    }
  };

  const handleProceedToCheckout = () => {
    if (cartItems.length === 0) return alert('Le panier est vide');
    setIsClosing(true);
    setTimeout(() => {
      closeCart();
      navigate('/checkout');
    }, 300);
  };

  const toggleItemExpanded = (itemKey) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  };

  if (loading) {
    return (
      <div className="shopping-cart-modal">
        <div className="shopping-cart-overlay" onClick={handleClose}></div>
        <div className="shopping-cart-content">
          <div className="shopping-cart-loading">
            <div className="loading-spinner"></div>
            <p>Chargement du panier...</p>
          </div>
        </div>
      </div>
    );
  }

  const subtotal = cartItems.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0);

  let deliveryFee = 0;
  let progress = 0;
  let showProgressBar = false;
  let freeDeliveryAchieved = false;

  if (deliverySettings?.is_enabled) {
    const minFree = Number(deliverySettings.min_free_delivery);
    if (minFree > 0) {
      freeDeliveryAchieved = subtotal >= minFree;
      deliveryFee = freeDeliveryAchieved ? 0 : Number(deliverySettings.delivery_fee || 0);
      if (!freeDeliveryAchieved) {
        showProgressBar = true;
        progress = Math.min(100, (subtotal / minFree) * 100);
      }
    } else {
      deliveryFee = Number(deliverySettings.delivery_fee || 0);
    }
  }

  const total = subtotal + deliveryFee;

  const renderSelectedOptions = (selectedOptions, options, itemKey) => {
    if (!selectedOptions || !options) return null;

    const optionsList = Object.entries(selectedOptions).map(([groupId, valueId]) => {
      const group = options.find(g => g.id === Number(groupId));
      if (!group) return null;
      const value = group.values.find(v => v.id === Number(valueId));
      if (!value) return null;
      return {
        groupName: group.name,
        valueName: value.value,
        extraPrice: value.extra_price > 0 ? Number(value.extra_price) : 0
      };
    }).filter(Boolean);

    if (optionsList.length === 0) return null;

    const isExpanded = expandedItems[itemKey];
    const shouldCollapse = optionsList.length > 2;

    return (
      <div className="shopping-cart-options-container">
        <div className="shopping-cart-options-wrapper">
          {shouldCollapse && !isExpanded ? (
            <>
              <div className="shopping-cart-option-pill">
                <Settings size={12} />
                <span className="shopping-cart-option-label">{optionsList[0].groupName}:</span>
                <span className="shopping-cart-option-value">{optionsList[0].valueName}</span>
                {optionsList[0].extraPrice > 0 && (
                  <span className="shopping-cart-option-price">+DT {optionsList[0].extraPrice.toFixed(2)}</span>
                )}
              </div>
              <button 
                className="shopping-cart-options-toggle"
                onClick={() => toggleItemExpanded(itemKey)}
              >
                <span>+{optionsList.length - 1} de plus</span>
                <ChevronDown size={14} />
              </button>
            </>
          ) : (
            <>
              {optionsList.map((option, index) => (
                <div key={index} className="shopping-cart-option-pill">
                  {index === 0 && <Settings size={12} />}
                  <span className="shopping-cart-option-label">{option.groupName}:</span>
                  <span className="shopping-cart-option-value">{option.valueName}</span>
                  {option.extraPrice > 0 && (
                    <span className="shopping-cart-option-price">+DT {option.extraPrice.toFixed(2)}</span>
                  )}
                </div>
              ))}
              {shouldCollapse && isExpanded && (
                <button 
                  className="shopping-cart-options-toggle"
                  onClick={() => toggleItemExpanded(itemKey)}
                >
                  <span>Afficher moins</span>
                  <ChevronUp size={14} />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`shopping-cart-modal ${isClosing ? 'closing' : ''}`}>
      <div className="shopping-cart-overlay" onClick={handleClose}></div>
      <div className="shopping-cart-content">
        <div className="shopping-cart-header">
          <div className="shopping-cart-header-content">
            <div className="shopping-cart-header-left">
              <ShoppingCart size={24} strokeWidth={2.5} />
              <div>
                <h2 className="shopping-cart-title">Mon panier</h2>
                <p className="shopping-cart-subtitle">{cartItems.length} {cartItems.length === 1 ? 'article' : 'articles'}</p>
              </div>
            </div>
            <button onClick={handleClose} className="shopping-cart-close-button">
              <X size={20} />
            </button>
          </div>
        </div>

        {cartItems.length === 0 ? (
          <div className="shopping-cart-empty-state">
            <div className="shopping-cart-empty-icon-wrapper">
              <ShoppingCart size={48} strokeWidth={1.5} />
            </div>
            <h3 className="shopping-cart-empty-text">Votre panier est vide</h3>
            <p className="shopping-cart-empty-subtitle">Ajoutez des articles pour commencer</p>
            <button onClick={handleClose} className="shopping-cart-empty-button">Continuer les achats</button>
          </div>
        ) : (
          <>
            <div className="shopping-cart-items-list">
              {cartItems.map((item) => {
                const itemTotal = (item.price || 0) * item.quantity;
                const itemKey = `${item.productId}-${JSON.stringify(item.selected_options)}`;

                return (
                  <div key={itemKey} className="shopping-cart-item">
                    <div className="shopping-cart-item-image-wrapper">
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          className="shopping-cart-item-image"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="shopping-cart-item-image-placeholder">
                          <Package size={28} strokeWidth={1.5} />
                        </div>
                      )}
                    </div>

                    <div className="shopping-cart-item-info">
                      <div className="shopping-cart-item-header">
                        <div className="shopping-cart-item-title-section">
                          <h4 className="shopping-cart-item-name">{item.name}</h4>
                          {renderSelectedOptions(item.selected_options, item.options, itemKey)}
                        </div>
                        <button
                          onClick={() => handleDeleteItem(item.productId)}
                          className="shopping-cart-remove-button"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      <div className="shopping-cart-item-footer">
                        <div className="shopping-cart-quantity-control">
                          <button
                            onClick={() => handleUpdateQuantity(item.productId, item.quantity - 1, item.selected_options)}
                            disabled={item.quantity <= 1}
                            className="shopping-cart-quantity-button"
                          >
                            <Minus size={14} />
                          </button>
                          <span className="shopping-cart-quantity-value">{item.quantity}</span>
                          <button
                            onClick={() => handleUpdateQuantity(item.productId, item.quantity + 1, item.selected_options)}
                            className="shopping-cart-quantity-button"
                          >
                            <Plus size={14} />
                          </button>
                        </div>

                        <div className="shopping-cart-item-pricing">
                          <span className="shopping-cart-item-total">DT {itemTotal.toFixed(2)}</span>
                          {item.quantity > 1 && (
                            <span className="shopping-cart-item-unit-price">DT {(item.price || 0).toFixed(2)} l'unite</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="shopping-cart-footer">
              {showProgressBar && (
                <div className="shopping-cart-progress-section">
                  <div className="shopping-cart-progress-header">
                    <Truck size={16} />
                    <span>Ajoutez DT {(deliverySettings.min_free_delivery - subtotal).toFixed(2)} de plus pour la livraison gratuite</span>
                  </div>
                  <div className="shopping-cart-progress-bar">
                    <div className="shopping-cart-progress-fill" style={{ width: `${progress}%` }}></div>
                  </div>
                </div>
              )}

              {freeDeliveryAchieved && (
                <div className="shopping-cart-free-delivery-message">
                  <Check size={16} />
                  <span>Vous avez debloque la livraison gratuite !</span>
                </div>
              )}

              <div className="shopping-cart-summary">
                <div className="shopping-cart-summary-row">
                  <span>Sous-total</span>
                  <span>DT {subtotal.toFixed(2)}</span>
                </div>
                {deliverySettings?.is_enabled && (
                  <div className="shopping-cart-summary-row">
                    <span>Livraison</span>
                    <span>{deliveryFee === 0 ? 'Gratuite' : `DT ${deliveryFee.toFixed(2)}`}</span>
                  </div>
                )}
                <div className="shopping-cart-divider"></div>
                <div className="shopping-cart-summary-row shopping-cart-total-row">
                  <span>Total</span>
                  <span>DT {total.toFixed(2)}</span>
                </div>
              </div>

              <button onClick={handleProceedToCheckout} className="shopping-cart-checkout-button">
                <span>Passer au paiement</span>
                <CreditCard size={18} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Cart;
